/**
 * @fileoverview Servicio de gestión de alertas - Patrón arquitectónico SOLID 
 * Implementa URLs absolutas, manejo defensivo de errores, y diagnóstico avanzado
 * siguiendo principios de diseño de software limpio y Domain-Driven Design
 * 
 * @module alertService
 * @author Nubelytics Engineering
 */
import { buildApiUrl, buildSyncUrl } from '../utils/apiUrlBuilder';
import api from './apiClient';
import appConfig from '../infrastructure/config/AppConfig';

/**
 * Construye URL absoluta para endpoints de alertas utilizando la configuración centralizada
 * @param {string} endpoint - Endpoint relativo (ej: '/123')
 * @returns {string} URL absoluta correcta según el entorno
 */
const getAlertUrl = (endpoint = '') => {
  // Normalizar el endpoint (eliminar barra inicial si existe)
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  
  // Usar buildApiUrl que maneja correctamente la construcción de URLs con subdominios
  // siguiendo la nueva arquitectura de microservicios
  const resourcePath = `alerts/${normalizedEndpoint}`;
  const absoluteUrl = buildApiUrl(resourcePath);
  
  // Registrar para diagnóstico avanzado
  try {
    localStorage.setItem('nubelytics_alerts_endpoint', endpoint);
    localStorage.setItem('nubelytics_alerts_resource_path', resourcePath);
    localStorage.setItem('nubelytics_alerts_absolute_url', absoluteUrl);
    console.debug('[alertService] URL construida:', absoluteUrl);
  } catch (e) {/* Error no crítico */}
  
  return absoluteUrl;
};

/**
 * Obtiene todas las alertas activas con manejo defensivo ante respuestas malformadas
 * @returns {Promise<{data: Array}>} Alertas normalizadas
 */
export const fetchAlerts = async () => {
  try {
    // Construir URL absoluta usando apiUrlBuilder para garantizar formato correcto
    const alertsUrl = getAlertUrl();
    
    // Forzar headers específicos para garantizar respuesta JSON
    // Incluir storeId explícito para garantizar que backend lo reciba aunque el header aún no esté disponible
    const storeId = (await import('./userDataService.js')).default?.getStoreId?.() ?? null;

    const response = await api.get(alertsUrl, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',  // Previene respuestas HTML en muchos frameworks
        'Cache-Control': 'no-cache'  // Evita respuestas cacheadas que podrían ser HTML
      },
      // Añadir timestamp y storeId para evitar cacheo y errores de backend
      params: {
        _t: new Date().getTime(),
        ...(storeId ? { storeId } : {})
      }
    });
    
    // Registrar diagnóstico detallado
    try {
      localStorage.setItem('nubelytics_alerts_url_actual', response.config.url);
      localStorage.setItem('nubelytics_alerts_timestamp', new Date().toISOString());
      localStorage.setItem('nubelytics_alerts_response_type', typeof response.data);
    } catch (e) {/* Error no crítico */}
    
    // Validación exhaustiva de la integridad de datos con diagnóstico avanzado
    if (response && typeof response === 'object') {
      const isHTML = typeof response.data === 'string' && 
                   (response.data.includes('<!DOCTYPE') || response.data.includes('<html') || 
                    response.data.includes('&lt;!DOCTYPE') || response.data.includes('&lt;html'));
      
      // Diagnóstico detallado para respuestas inesperadas
      if (isHTML) {
        console.warn('[alertService] Respuesta HTML detectada en fetchAlerts');
        try {
          // Guardar información diagnóstica más completa
          localStorage.setItem('nubelytics_alerts_html_response', 
                              response.data.substring(0, 500));
          localStorage.setItem('nubelytics_alerts_status', response.status);
          localStorage.setItem('nubelytics_alerts_content_type', response.headers?.['content-type'] || 'desconocido');
          
          // Intentar recuperación: probar nuevamente con endpoint alternativo si falló el relativo
          console.log('[alertService] Intentando recuperación con endpoint alternativo...');
          return await fetchAlertsAlternative();
        } catch (e) {
          console.error('[alertService] Error en recuperación:', e);
        }
      }
      
      // --- Normalización de la estructura de datos ---
      let alertsArray = [];
      if (Array.isArray(response.data)) {
        alertsArray = response.data;
      } else if (response.data && Array.isArray(response.data.alerts)) {
        // Respuesta con objeto { alerts: [...] }
        alertsArray = response.data.alerts;
      }
      
      // Garantizar estructura uniforme para UI
      return {
        ...response,
        data: alertsArray
      };
    }
    return { data: [] };
  } catch (error) {
    console.error('[alertService] Error obteniendo alertas:', error);
    // Añadir diagnóstico detallado del error
    try {
      localStorage.setItem('nubelytics_alerts_error', error.message);
      localStorage.setItem('nubelytics_alerts_error_status', error.response?.status || 'sin status');
      localStorage.setItem('nubelytics_alerts_error_timestamp', new Date().toISOString());
    } catch (e) {/* Error no crítico */}
    
    // Intentar recuperación con endpoint alternativo
    console.log('[alertService] Intentando recuperación tras error...');
    return await fetchAlertsAlternative().catch(() => ({ data: [], error: true }));
  }
};

/**
 * Estrategia alternativa para obtener alertas - implementa patrón Strategy
 * @returns {Promise<{data: Array}>} Alertas normalizadas
 * @private
 */
const fetchAlertsAlternative = async () => {
  try {
    // Usar el microservicio de sincronización sync.nubelytics.com
    // con la función especializada buildSyncUrl
    // Construir URL absoluta al microservicio sync garantizada
  const syncEndpoint = buildSyncUrl('alerts');
  
  // Registrar diagnóstico para depuración
  try {
    localStorage.setItem('nubelytics_alerts_alternative_url', syncEndpoint);
    localStorage.setItem('nubelytics_alerts_alternative_timestamp', new Date().toISOString());
  } catch (e) {/* Error no crítico */}
    
    console.log('[alertService] Probando endpoint de microservicio sync:', syncEndpoint);
    
    // Si appConfig tiene disponible getSyncUrl, usarlo como primera opción
    // Esta es una mejora para aprovechar la nueva funcionalidad
    const syncUrlFromConfig = appConfig.getSyncUrl ? 
      appConfig.getSyncUrl('alerts') : syncEndpoint;
      
    console.log('[alertService] URL de sincronización desde config:', syncUrlFromConfig);
    
    // Usar URL absoluta garantizada con protocolo HTTPS
  const finalSyncUrl = syncUrlFromConfig || syncEndpoint;
  const absoluteUrl = finalSyncUrl.startsWith('http') 
    ? finalSyncUrl 
    : `https://sync.nubelytics.com/${finalSyncUrl.replace(/^\//, '')}`;
    
  const response = await api.get(absoluteUrl, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Recovery-Attempt': 'true',
        'X-No-Html': 'true'
      }
    });
    
    return {
      ...response,
      data: Array.isArray(response.data) ? response.data : []
    };
  } catch (error) {
    console.error('[alertService] Error en estrategia alternativa:', error);
    return { data: [], error: true, recovery: false };
  }
};


/**
 * Crea una nueva alerta con validación de estructura
 * @param {Object} data - Datos de la alerta a crear
 * @returns {Promise<Object>} Resultado de la operación
 */
export const createAlert = async (data) => {
  try {
    // Validación previa para evitar errores del servidor
    if (!data || typeof data !== 'object') {
      console.error('[alertService] Intento de crear alerta con datos inválidos');
      return { error: true, message: 'Datos de alerta inválidos' };
    }
    
    // Asegurar que enviamos storeId en el payload
    const storeId = (await import('./userDataService.js')).default?.getStoreId?.() ?? null;
    const payload = storeId && !data.storeId ? { ...data, storeId } : data;
    const response = await api.post(getAlertUrl(), payload);
    return response?.data || { success: true };
  } catch (error) {
    console.error('[alertService] Error creando alerta:', error);
    return { 
      error: true, 
      message: error.message || 'Error al crear la alerta', 
      status: error.response?.status
    };
  }
};

/**
 * Actualiza una alerta existente
 * @param {string|number} id - Identificador de la alerta
 * @param {Object} data - Nuevos datos para la alerta
 * @returns {Promise<Object>} Resultado de la operación
 */
export const updateAlert = async (id, data) => {
  try {
    // Validaciones defensivas
    if (!id) {
      return { error: true, message: 'Se requiere ID de alerta' };
    }
    
    if (!data || typeof data !== 'object') {
      return { error: true, message: 'Datos inválidos para actualización' };
    }
    
    const response = await api.put(getAlertUrl(`/${id}`), data);
    return response?.data || { success: true };
  } catch (error) {
    console.error(`[alertService] Error actualizando alerta ${id}:`, error);
    return { 
      error: true, 
      message: error.message || `Error al actualizar la alerta ${id}`
    };
  }
};

/**
 * Elimina una alerta
 * @param {string|number} id - Identificador de la alerta a eliminar
 * @returns {Promise<Object>} Resultado de la operación
 */
export const deleteAlert = async (id) => {
  try {
    // Validación defensiva
    if (!id) {
      return { error: true, message: 'Se requiere ID de alerta para eliminar' };
    }
    
    const response = await api.delete(getAlertUrl(`/${id}`));
    return response?.data || { success: true };
  } catch (error) {
    console.error(`[alertService] Error eliminando alerta ${id}:`, error);
    return { 
      error: true, 
      message: error.message || `Error al eliminar la alerta ${id}`
    };
  }
};
